import {createSelector} from 'reselect';

import FEATURE_FLAGS from './featureFlags';

/**
 * Selects the global configuration state from the application state.
 * @param {Object} state - The global state.
 * @return {Object} The global configuration state.
 */
const gsConfigSelector = (state) => state?.gsConfig || {};

/**
 * Selects the feature flags from the global configuration state.
 * @param {Object} state - The global state.
 * @return {Object} The feature flags.
 */
const featureFlagsSelector = createSelector(gsConfigSelector, (gsConfig) => gsConfig.features || {});

/**
 * Selects the UK CMA features flag from the feature flags.
 * @param {Object} state - The global state.
 * @return {boolean} `true` if the UK CMA features are enabled, otherwise `false`.
 */
const ukcmaSelector = createSelector(
  featureFlagsSelector,
  (features = {}) => !!features[FEATURE_FLAGS.homepage.ukCmaFeatures]
);

/**
 * Selects the redirect modal feature flag from the feature flags.
 * @param {Object} state - The global state.
 * @return {boolean} `true` if the redirect modal is enabled, otherwise `false`.
 */
const isRedirectModalEnabledSelector = createSelector(
  featureFlagsSelector,
  (features = {}) => !!features[FEATURE_FLAGS.common.showRedirectModal]
);

/**
 * Selects the hide CBT events feature flag from the feature flags.
 * @param {Object} state - The global state.
 * @return {boolean} `true` if the CBT events are hidden, otherwise `false`.
 */
const hideCbtEventsSelector = createSelector(
  featureFlagsSelector,
  (features = {}) => !!features[FEATURE_FLAGS.common.hideCbtEvents]
);

/**
 * Selector for retrieving the device state.
 * @param {Object} state - The entire Redux state.
 * @returns {Object} The device state.
 */
export const deviceSelector = (state) => state?.device || null;

/**
 * Selector for retrieving the device type.
 * @param {Object} state - The entire Redux state.
 * @returns {string} The device type.
 */
const deviceTypeSelector = createSelector(deviceSelector, (device = {}) => device?.type || null);

/**
 * Selector for retrieving the search suggestions.
 * @param {Object} localState - The local Redux state.
 * @returns {Array<Object>} The list of search suggestions.
 */
const searchSuggestionsSelector = createSelector(
  (localState) => localState?.searchSuggestions || [],
  (searchSuggestions) => searchSuggestions
);

/**
 * Selector for retrieving the viewport state.
 * @param {Object} state - The entire Redux state.
 * @returns {Object} The viewport state.
 */
const viewportStateSelector = createSelector(
  (state) => state?.viewport || {},
  (viewport) => viewport
);

/**
 * Selector for retrieving the user's location.
 * @param {Object} state - The entire Redux state.
 * @returns {Object|null} The user's location or null if not available.
 */
const userLocationSelector = createSelector(
  (state) => state?.user || {},
  (user) => user?.location || null
);

/**
 * Selector for retrieving the user's location as a string of latitude and longitude.
 * @param {Object} state - The entire Redux state.
 * @returns {string|null} The user's location as "latitude,longitude" or null if not available.
 */
const pointSelector = createSelector(userLocationSelector, (location) =>
  location ? `${location.latitude},${location.longitude}` : null
);

export {
  featureFlagsSelector,
  ukcmaSelector,
  isRedirectModalEnabledSelector,
  hideCbtEventsSelector,
  deviceTypeSelector,
  searchSuggestionsSelector,
  viewportStateSelector,
  userLocationSelector,
  pointSelector,
};
